import React, { FunctionComponent, useContext, useMemo } from "react";
import { fade, Grid, makeStyles, Theme, Typography } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import { NutritionContext } from "../../pages/nutrition/context/nutrition-context";

import {
  CATEGORY_MENU_FONT_SIZE,
  CATEGORY_MENU_PADDING_SPACING_X,
  CATEGORY_MENU_PADDING_SPACING_Y,
} from "./category-item-menu";
import { ArrowRightIcon } from "../../assets/icons/arrows/arrow-right-icon";
import { MenuItemCategory, MenuItemSubcategory } from "../../models/types/category-types";
import { DARK_TEXT_COLOR } from "../../theme/theme";
import { useUser } from "../user/user-context";
import { MenuSubcategoryDictionary } from "../../models/categories";

const PASSIVE_OPACITY = 0;
const ACTIVE_OPACITY = 0.3;

interface ICategoryItemMenuItemStyleProps {
  activeColor: string;
  active: boolean;
  disabled: boolean;
}

const useStyles = makeStyles((theme: Theme) => ({
  menuItem: {
    borderRadius: theme.spacing(2),
    textAlign: "start",
    backgroundColor: (props: ICategoryItemMenuItemStyleProps) =>
      fade(props.activeColor, props.active ? ACTIVE_OPACITY : PASSIVE_OPACITY),
    "&:hover": {
      backgroundColor: (props: ICategoryItemMenuItemStyleProps) =>
        !props.disabled ? fade(props.activeColor, ACTIVE_OPACITY) : ``,
    },
    transition: "all 0.2s",
    paddingLeft: theme.spacing(CATEGORY_MENU_PADDING_SPACING_X),
    paddingRight: theme.spacing(CATEGORY_MENU_PADDING_SPACING_X),
    paddingTop: theme.spacing(CATEGORY_MENU_PADDING_SPACING_Y),
    paddingBottom: theme.spacing(CATEGORY_MENU_PADDING_SPACING_Y),
  },
  menuItemContainer: {
    cursor: ({ disabled }: ICategoryItemMenuItemStyleProps) => (disabled ? "not-allowed" : "pointer"),
    paddingLeft: theme.spacing(CATEGORY_MENU_PADDING_SPACING_X / 2),
    paddingRight: theme.spacing(CATEGORY_MENU_PADDING_SPACING_X / 2),
    paddingTop: theme.spacing(CATEGORY_MENU_PADDING_SPACING_Y / 2),
    paddingBottom: theme.spacing(CATEGORY_MENU_PADDING_SPACING_Y / 2),
  },
  itemText: {
    color: ({ disabled }: ICategoryItemMenuItemStyleProps) => fade(DARK_TEXT_COLOR, disabled ? ACTIVE_OPACITY : 1),
    fontSize: CATEGORY_MENU_FONT_SIZE,
    fontWeight: 300,
  },
}));

interface ICategoryItemMenuItemProps {
  subcategory: MenuItemSubcategory;
  category: MenuItemCategory;
}

export const CategoryItemMenuItem: FunctionComponent<ICategoryItemMenuItemProps> = (props) => {
  const { subcategory, category } = props;
  const { changeActiveCategories, activeSubcategory } = useContext(NutritionContext);
  const { isDemo } = useUser();

  const { t } = useTranslation();
  const active = useMemo(() => subcategory === activeSubcategory, [subcategory, activeSubcategory]);
  const disabled = useMemo(() => isDemo && !MenuSubcategoryDictionary[subcategory].enabledForDemo, [
    subcategory,
    isDemo,
  ]);

  const classes = useStyles({ activeColor: "#FFFFFF", active, disabled });

  return (
    <Grid className={classes.menuItemContainer}>
      <Grid
        item
        container
        className={classes.menuItem}
        justify="space-between"
        key={subcategory}
        onClick={() => (!disabled ? changeActiveCategories(category, subcategory) : () => {})}
      >
        <Typography className={classes.itemText}>{t(`menu_item_subcategories.${subcategory}`)}</Typography>
        <Typography className={classes.itemText}>
          <ArrowRightIcon
            color={fade(DARK_TEXT_COLOR, disabled ? ACTIVE_OPACITY : 1)}
            width={CATEGORY_MENU_FONT_SIZE - 3}
          />
        </Typography>
      </Grid>
    </Grid>
  );
};
